import React, { useState } from "react";
import {
  FiUser,
  FiMapPin,
  FiHash,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import Statemaster from "./Statemaster.json";

const ShopDetailsSection = ({ onSave, onEdit }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  const [form, setForm] = useState({
    shopName: "",
    state: "",
    district: "",
    landmark: "",
    pincode: "",
    shopAddress: "",
    gstNumber: "",
  });

  const [errors, setErrors] = useState({});

  /* ---------------- VALIDATION ---------------- */

  const validate = () => {
    const e = {};
    if (!form.shopName) e.shopName = "Required";
    if (!form.state) e.state = "Required";
    if (!form.district) e.district = "Required";
    if (!form.shopAddress) e.shopAddress = "Required";
    if (!form.pincode) e.pincode = "Required";
    if (!form.gstNumber) e.gstNumber = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- SAVE ---------------- */

  const handleSave = () => {
    if (!validate()) return;
    setIsSaved(true);
    onSave(form);
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = () => {
    setIsSaved(false);
    onEdit();
  };

  const disabled = isSaved;

  /* ---------------- UI ---------------- */

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* HEADER */}
      <div
        className="flex cursor-pointer items-center justify-between border-b px-6 py-4"
        onClick={() => setIsOpen((p) => !p)}
      >
        <h3 className="text-sm font-semibold text-gray-800">
          Shop Information
        </h3>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </div>

      {isOpen && (
        <div className="p-6">
          {/* INPUT GRID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Shop Name */}
            <div>
              <label className="text-xs text-gray-600">Shop Name *</label>
              <div className="relative mt-1">
                <FiUser className="absolute left-3 top-3 text-gray-400" />
                <input
                  disabled={disabled}
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  value={form.shopName}
                  onChange={(e) =>
                    setForm({ ...form, shopName: e.target.value })
                  }
                />
              </div>
              {errors.shopName && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.shopName}
                </p>
              )}
            </div>

            {/* GST */}
            <div>
              <label className="text-xs text-gray-600">GST Number *</label>
              <div className="relative mt-1">
                <FiHash className="absolute left-3 top-3 text-gray-400" />
                <input
                  disabled={disabled}
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  value={form.gstNumber}
                  onChange={(e) =>
                    setForm({ ...form, gstNumber: e.target.value })
                  }
                />
              </div>
              {errors.gstNumber && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.gstNumber}
                </p>
              )}
            </div>

            {/* State */}
            <div>
              <label className="text-xs text-gray-600">State *</label>
              <select
                disabled={disabled}
                className="w-full rounded-md border px-3 py-2 text-sm mt-1"
                value={form.state}
                onChange={(e) =>
                  setForm({
                    ...form,
                    state: e.target.value,
                    district: "",
                  })
                }
              >
                <option value="">Select State</option>
                {Statemaster.states.map((s) => (
                  <option key={s.state} value={s.state}>
                    {s.state}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.state}
                </p>
              )}
            </div>

            {/* District */}
            <div>
              <label className="text-xs text-gray-600">District *</label>
              <select
                disabled={disabled}
                className="w-full rounded-md border px-3 py-2 text-sm mt-1"
                value={form.district}
                onChange={(e) =>
                  setForm({ ...form, district: e.target.value })
                }
              >
                <option value="">Select District</option>
                {Statemaster.states
                  .find((s) => s.state === form.state)
                  ?.districts.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
              </select>
              {errors.district && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.district}
                </p>
              )}
            </div>

            {/* Landmark */}
            <div>
              <label className="text-xs text-gray-600">Landmark</label>
              <div className="relative mt-1">
                <FiMapPin className="absolute left-3 top-3 text-gray-400" />
                <input
                  disabled={disabled}
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  value={form.landmark}
                  onChange={(e) =>
                    setForm({ ...form, landmark: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Pincode */}
            <div>
              <label className="text-xs text-gray-600">Pincode *</label>
              <input
                disabled={disabled}
                className="w-full rounded-md border px-3 py-2 text-sm mt-1"
                value={form.pincode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                  })
                }
              />
              {errors.pincode && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.pincode}
                </p>
              )}
            </div>

            {/* Shop Address */}
            <div className="md:col-span-2">
              <label className="text-xs text-gray-600">
                Shop Address *
              </label>
              <div className="relative mt-1">
                <FiMapPin className="absolute left-3 top-3 text-gray-400" />
                <input
                  disabled={disabled}
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  value={form.shopAddress}
                  onChange={(e) =>
                    setForm({ ...form, shopAddress: e.target.value })
                  }
                />
              </div>
              {errors.shopAddress && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.shopAddress}
                </p>
              )}
            </div>
          </div>

          {/* SAVE / EDIT */}
          <div className="mt-6">
            {!isSaved ? (
              <button
                onClick={handleSave}
                className="rounded-full bg-green-600 px-8 py-2 text-sm text-white"
              >
                Save
              </button>
            ) : (
              <button
                onClick={handleEdit}
                className="rounded-full bg-gray-500 px-8 py-2 text-sm text-white"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopDetailsSection;
